import { campaignObjectiveLabel, campaignStatusLabel, formatDate } from '@crm/shared';
import { Badge, Button, Card, CardContent, Spinner } from '@crm/ui';
import { ChevronRight, Megaphone, Plus, Upload } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  CAMPAIGNS_ROUTES,
  type Campaign,
  campaignPath,
  useCampaigns,
  useCreateCampaign,
} from '../api';

/** Com o que a campanha nova comeca - trocavel, mas nunca em branco. */
const PRIMEIRA_MENSAGEM =
  'Oi {{cliente}}! A {{unidade}} esta com horarios abertos. Use o cupom {{cupom}}.';

const CORES: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  ACTIVE: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  PAUSED: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  ARCHIVED: 'bg-muted text-muted-foreground',
};

/** O resumo de uma campanha em uma linha - a lista nao vira tabela. */
function resumo(campaign: Campaign): string {
  const partes = [campaign.dispatchLabel];

  if (campaign.filters.unitIds.length > 0) {
    partes.push(`${campaign.filters.unitIds.length} unidades`);
  }
  if (campaign.lastRunAt) partes.push(`ultimo disparo em ${formatDate(campaign.lastRunAt)}`);

  return partes.join(' - ');
}

export function CampaignsPage() {
  useDocumentTitle('Campanhas e mensagens automaticas');

  const navigate = useNavigate();
  const canWrite = useCan('customers', 'WRITE');
  const campaigns = useCampaigns();
  const createCampaign = useCreateCampaign();
  /**
   * A campanha nova abre o mesmo formulario de sempre, ja em rascunho.
   *
   * Perguntar o objetivo numa janela antes so adiantava a escolha que o
   * proprio passo Objetivo faz - e quem se enganasse ali tinha de voltar para
   * a lista e recomecar. Nasce manual por ser a primeira opcao do passo;
   * trocar para automatica e um clique.
   */
  function criar() {
    createCampaign.mutate(
      { objective: 'COUPON', body: PRIMEIRA_MENSAGEM },
      { onSuccess: (campaign) => navigate(campaignPath(campaign.id)) },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Megaphone />}
        title="Campanhas e mensagens automaticas"
        description="Mensagens de WhatsApp para um publico escolhido."
        actions={
          canWrite ? (
            <>
              <Button variant="outline" asChild>
                <Link to={CAMPAIGNS_ROUTES.IMPORT}>
                  <Upload className="h-4 w-4" />
                  <span>
                    Importar<span className="hidden sm:inline"> contatos</span>
                  </span>
                </Link>
              </Button>
              <Button onClick={criar} disabled={createCampaign.isPending}>
                {createCampaign.isPending ? <Spinner /> : <Plus className="h-4 w-4" />}
                <span>
                  Nova<span className="hidden sm:inline"> campanha</span>
                </span>
              </Button>
            </>
          ) : null
        }
      />

      {createCampaign.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(createCampaign.error)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="p-0">
          {campaigns.isPending ? (
            <div className="flex justify-center py-14">
              <Spinner />
            </div>
          ) : campaigns.data?.data.length ? (
            <ul className="divide-y divide-border">
              {campaigns.data.data.map((campaign) => (
                <li key={campaign.id}>
                  <Link
                    to={campaignPath(campaign.id)}
                    className="flex items-center gap-3 py-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium">{campaign.name}</span>
                        <Badge className={CORES[campaign.status]}>
                          {campaignStatusLabel(campaign.status)}
                        </Badge>
                        <Badge className="bg-muted text-muted-foreground">
                          {campaignObjectiveLabel(campaign.objective)}
                        </Badge>
                      </div>
                      <p className="truncate text-sm text-muted-foreground">{resumo(campaign)}</p>
                    </div>

                    <div className="hidden shrink-0 text-right text-sm sm:block">
                      <p className="font-medium">{campaign.sentTotal} enviadas</p>
                      <p className="text-muted-foreground">{campaign.scheduledTotal} agendaram</p>
                    </div>

                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Megaphone />} title="Nenhuma campanha por aqui" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
